/**
 * Advanced URL Generator Utilities
 *
 * This module provides functions for building advanced query parameters
 * for QR code URLs. Advanced parameters are only included when the
 * advanced options are enabled and individual options have non-default values.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */

import type { AdvancedOptions } from '../types/advancedOptions';
import { generateSiteQrUrl, generateBayQrUrl } from './urlGenerator';

/**
 * Builds advanced query parameters string.
 * Only includes parameters when:
 * 1. Advanced options are enabled (enabled=true)
 * 2. The specific option has a non-default value
 *
 * @param options - The advanced options configuration
 * @returns Query string starting with '&' if any params exist, empty string otherwise
 *
 * Requirement 6.1: WHEN any Advanced_Option has a non-default value,
 * THE URL_Generator SHALL include the corresponding query parameter
 *
 * Requirement 6.2: WHEN all Advanced_Options have default values,
 * THE URL_Generator SHALL generate URLs without advanced query parameters
 *
 * Requirement 6.3: THE URL_Generator SHALL properly encode all advanced
 * parameter values in the URL
 *
 * Requirement 6.5: WHEN the Advanced_Toggle is disabled, THE URL_Generator
 * SHALL NOT include any advanced query parameters regardless of option values
 */
export function buildAdvancedQueryParams(options: AdvancedOptions): string {
  if (!options.enabled) {
    return '';
  }

  const params: string[] = [];

  if (options.experimentalFeatures) {
    params.push('exp=true');
  }

  if (options.languageOverride !== null) {
    params.push(`lang=${encodeURIComponent(options.languageOverride)}`);
  }

  if (options.swingCapture) {
    params.push('swing-capture=true');
  }

  if (options.gameMode.trim() !== '') {
    params.push(`mode=${encodeURIComponent(options.gameMode)}`);
  }

  return params.length > 0 ? `&${params.join('&')}` : '';
}


/**
 * Generates the QR code URL for a site with key and advanced parameters.
 *
 * @param siteId - The unique identifier for the site
 * @param siteKey - The site key for URL validation
 * @param advancedOptions - The advanced options configuration
 * @returns The encoded URL for the site QR code with advanced parameters
 *
 * Requirement 6.4: THE URL_Generator SHALL maintain existing URL parameters
 * (siteId, k) when adding advanced parameters
 */
export function generateSiteQrUrlWithAdvanced(
  siteId: string,
  siteKey: string,
  advancedOptions: AdvancedOptions
): string {
  const baseUrl = generateSiteQrUrl(siteId, siteKey);
  const advancedParams = buildAdvancedQueryParams(advancedOptions);
  return `${baseUrl}${advancedParams}`;
}

/**
 * Generates the QR code URL for a bay with key and advanced parameters.
 *
 * @param siteId - The unique identifier for the site
 * @param bayId - The unique identifier for the bay
 * @param siteKey - The site key for URL validation
 * @param advancedOptions - The advanced options configuration
 * @returns The encoded URL for the bay QR code with advanced parameters
 *
 * Requirement 6.4: THE URL_Generator SHALL maintain existing URL parameters
 * (siteId, bayId, k) when adding advanced parameters
 */
export function generateBayQrUrlWithAdvanced(
  siteId: string,
  bayId: string,
  siteKey: string,
  advancedOptions: AdvancedOptions
): string {
  const baseUrl = generateBayQrUrl(siteId, bayId, siteKey);
  const advancedParams = buildAdvancedQueryParams(advancedOptions);
  return `${baseUrl}${advancedParams}`;
}
